import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, fmtQty } from "../../api";
import { Plus, Trash2 } from "lucide-react";
import MfgTabs from "../../components/MfgTabs";

export default function BOMForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ name: "", finished_product_id: "", basis_qty: 10000, basis_uom: "Meter", active: true, components: [] });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { active: "true" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/boms/${id}`).then((r) => {
      const b = r.data;
      setF({
        name: b.name, finished_product_id: b.finished_product_id, basis_qty: b.basis_qty,
        basis_uom: b.basis_uom, active: b.active,
        components: b.components.map((c) => ({ product_id: c.product_id, qty: c.qty, uom: c.uom })),
      });
      setHistory(b.history || []);
    }).catch((e) => toast.error(fmtErr(e)));
  }, [id, isEdit]);

  const finished = products.filter((p) => p.product_type === "Finished Good");
  const materials = products.filter((p) => p.id !== f.finished_product_id);

  const setComp = (i, k, v) => setF((p) => {
    const comps = [...p.components];
    comps[i] = { ...comps[i], [k]: v };
    if (k === "product_id") {
      const prod = products.find((x) => x.id === v);
      if (prod) comps[i].uom = prod.uom;
    }
    return { ...p, components: comps };
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, basis_qty: parseFloat(f.basis_qty), components: f.components.map((c) => ({ ...c, qty: parseFloat(c.qty) })) };
      if (isEdit) {
        await api.put(`/boms/${id}`, body);
        toast.success("BOM updated (new version saved)");
      } else {
        await api.post("/boms", body);
        toast.success("BOM created");
      }
      navigate("/manufacturing/boms");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bom-form-page" className="space-y-6 max-w-3xl">
      <h1 className="font-heading text-3xl font-extrabold text-[#002B5D] tracking-tight">{isEdit ? "Edit BOM" : "New BOM"}</h1>
      <MfgTabs />
      <form onSubmit={submit} className="sk-card p-6 space-y-6 mt-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="sk-label">BOM Name / Version Label</label>
            <input data-testid="bom-name-input" className="sk-input" placeholder="Auto if empty" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Finished Product *</label>
            <select data-testid="bom-product-select" required disabled={isEdit} className="sk-input" value={f.finished_product_id} onChange={(e) => setF({ ...f, finished_product_id: e.target.value })}>
              <option value="">Select product...</option>
              {finished.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
            </select>
          </div>
          <div>
            <label className="sk-label">Production Quantity Basis *</label>
            <input data-testid="bom-basis-qty-input" type="number" step="any" min="0.001" required className="sk-input font-mono" value={f.basis_qty} onChange={(e) => setF({ ...f, basis_qty: e.target.value })} />
          </div>
          <div>
            <label className="sk-label">Basis UOM</label>
            <input data-testid="bom-basis-uom-input" className="sk-input" value={f.basis_uom} onChange={(e) => setF({ ...f, basis_uom: e.target.value })} />
          </div>
        </div>
        <p className="text-xs text-slate-500">Component quantities below are required per <span className="font-mono font-semibold">{fmtQty(parseFloat(f.basis_qty) || 0)} {f.basis_uom}</span> of production. The system scales them automatically.</p>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="sk-label mb-0">Components / Raw Materials *</label>
            <button type="button" data-testid="bom-add-component" onClick={() => setF((p) => ({ ...p, components: [...p.components, { product_id: "", qty: "", uom: "" }] }))}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#EB1C27]">
              <Plus className="w-4 h-4" /> Add Component
            </button>
          </div>
          <div className="space-y-2">
            {f.components.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_110px_110px_40px] gap-2 items-center" data-testid={`bom-component-row-${i}`}>
                <select data-testid={`bom-component-product-${i}`} required className="sk-input" value={c.product_id} onChange={(e) => setComp(i, "product_id", e.target.value)}>
                  <option value="">Material...</option>
                  {materials.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
                </select>
                <input data-testid={`bom-component-qty-${i}`} type="number" step="any" min="0.0001" required placeholder="Qty" className="sk-input font-mono" value={c.qty} onChange={(e) => setComp(i, "qty", e.target.value)} />
                <input data-testid={`bom-component-uom-${i}`} placeholder="UOM" className="sk-input" value={c.uom} onChange={(e) => setComp(i, "uom", e.target.value)} />
                <button type="button" data-testid={`bom-component-remove-${i}`} onClick={() => setF((p) => ({ ...p, components: p.components.filter((_, j) => j !== i) }))}
                  className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {f.components.length === 0 && <div className="text-sm text-slate-400 border border-dashed border-border rounded-md px-4 py-6 text-center">No components yet</div>}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input data-testid="bom-active-checkbox" type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4" />
          Active (usable for new Manufacturing Orders)
        </label>

        <button data-testid="bom-save-button" disabled={busy || f.components.length === 0}
          className="w-full h-12 rounded-md font-heading font-bold text-white disabled:opacity-50" style={{ backgroundColor: "#EB1C27" }}>
          {busy ? "Saving..." : isEdit ? "Save New Version" : "Create BOM"}
        </button>
      </form>

      {history.length > 0 && (
        <div className="sk-card p-5" data-testid="bom-history">
          <h3 className="font-heading font-bold text-[#002B5D] mb-3">Version History</h3>
          <div className="space-y-2 text-sm">
            {[...history].reverse().map((h, i) => (
              <div key={i} className="border border-border rounded-md px-4 py-2">
                <span className="font-mono font-semibold">v{h.version}</span>
                <span className="text-slate-500 ml-2 text-xs">replaced {fmtDate(h.replaced_at)} by {h.replaced_by}</span>
                <div className="text-xs text-slate-500 mt-1">{h.components.map((c) => `${c.qty} ${c.uom}`).join(" · ")} per {fmtQty(h.basis_qty)} {h.basis_uom}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
